import React, { memo, ReactNode, useEffect, useMemo } from 'react'
import { Button, Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { useModalContainer } from 'src/context/ModalContainerContext'

interface Props {
  isOpen?: boolean
  title?: string
  text?: string
  subtext?: string
  image?: any
  buttons?: ReactNode
  className?: any
  children?: ReactNode
  onClose(): void
  closeButtonTestId?: string
}

const CommonModalComponent = ({
  isOpen,
  title,
  text,
  subtext,
  image,
  buttons,
  className,
  children,
  onClose,
  closeButtonTestId,
}: Props) => {
  const contextContainer = useModalContainer()
  const container = useMemo(
    () => contextContainer ?? document.getElementById('root'),
    [contextContainer],
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const scrollableEl = document.querySelector('main') as HTMLElement | null

    if (!scrollableEl) {
      return
    }

    const prev = scrollableEl.style.overflowY

    scrollableEl.style.overflowY = 'hidden'

    return () => {
      scrollableEl.style.overflowY = prev
    }
  }, [isOpen])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
    >
      <div
        css={[
          tw`flex w-full max-w-[358px] max-h-[calc(100vh-64px)] flex-col bg-shades-0 rounded-2xl [overflow-wrap:break-word] [word-break:break-word] [min-width:0]`,
          className,
        ]}
      >
        {title || text || subtext ? (
          <div css={tw`shrink-0 px-5 pb-3 pt-5`}>
            {title && (
              <Typography css={tw`mb-1`} variant="h4">
                {title}
              </Typography>
            )}
            {text && <Typography css={tw`text-shades-400`}>{text}</Typography>}
            {subtext && <Typography css={tw`mt-2 text-shades-400`}>{subtext}</Typography>}
          </div>
        ) : null}
        {/* Scrolls on its own so a tall form can never push the footer off-screen or overlap it. */}
        <div
          css={[
            tw`min-h-0 flex-1 overflow-y-auto px-5 pb-1`,
            !(title || text || subtext) && tw`pt-5`,
          ]}
        >
          {image && <img css={tw`mb-6 w-full max-h-[60vh]`} src={image} />}
          {children}
        </div>
        <div
          css={tw`flex shrink-0 flex-row items-center justify-end gap-2 border-0 border-t border-solid border-shades-100 px-5 py-4 mt-4`}
        >
          {buttons || (
            <Button
              fullWidth
              onClick={onClose}
              variant="contained"
              css={tw`bg-shades-1000`}
              {...(closeButtonTestId ? { 'data-testid': closeButtonTestId } : {})}
            >
              <Typography css={tw`text-shades-0`}>Close</Typography>
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export const CommonModal = memo(CommonModalComponent)
